import { z } from 'zod';

/**
 * The three operations an operator can book by hand from the New Operation
 * screen. Incoming ships with #271; the other two with #272 and #273.
 */
export const OPERATION_KIND = {
  INCOMING: 'incoming',
  CONVERSION: 'conversion',
  PAYMENT: 'payment',
} as const;

export type OperationKind = (typeof OPERATION_KIND)[keyof typeof OPERATION_KIND];

/** `numeric(20,4)`, non-negative, as the wallet endpoints accept it. */
const decimalString = (field: string) =>
  z
    .string()
    .regex(/^\d{1,16}(\.\d{1,4})?$/, `${field} must be an amount with up to 4 decimal places`);

/**
 * Optional free text, bounded the way the endpoint bounds it. Measured once
 * trimmed, because that is what gets sent: padding the operator never meant
 * to type should not be what trips the limit.
 */
const optionalText = (field: string, max: number) =>
  z
    .string()
    .optional()
    .refine((v) => (v ?? '').trim().length <= max, `${field} must be ${max} characters or fewer`);

/**
 * The incoming-wire form.
 *
 * Two amounts, because a wire is rarely worth on arrival what the sender
 * posted: `amountSent` is what left them, `amountReceived` is what reached
 * the Montelux nostro, and the gap is what the correspondents took. Only
 * the second one moves the ledger. The fee is Montelux's own cut of it, and
 * what remains is credited to the client.
 */
export const NewIncomingFormSchema = z
  .object({
    clientId: z.number({ required_error: 'Pick a client' }),
    receivedDate: z.string().min(1, 'Date is required'),
    currencyCode: z.string().length(3, 'Currency is required'),
    amountSent: z.union([decimalString('Amount sent'), z.literal('')]).optional(),
    amountReceived: decimalString('Amount received').refine(
      (v) => Number(v) > 0,
      'Amount received must be more than zero',
    ),
    fee: z.union([decimalString('Fee'), z.literal('')]).optional(),
    counterparty: z.string().max(50).optional(),
    // Where the wire came from. Kept even when the client sent it to
    // themselves: their own external account is still an account at a bank.
    senderAccount: optionalText('Account number', 50),
    senderSwift: optionalText('Bank code', 50),
    senderBank: optionalText('Bank name', 50),
    reference: optionalText('Reference', 50),
    description: z.string().max(255).optional(),
    sameClient: z.boolean(),
  })
  .refine((v) => v.sameClient || (v.counterparty ?? '').trim().length > 0, {
    // @Daniel, #271 : « Counterparty (Sender) is of course not optional. It
    // is filled unless the checkbox is clicked. »
    message: 'Name the sender, or tick the box below if the client sent it to themselves',
    path: ['counterparty'],
  })
  .refine((v) => Number(v.fee || '0') <= Number(v.amountReceived || '0'), {
    // Crediting a negative amount is not an operation anyone meant to book.
    message: 'The fee cannot exceed what Montelux received',
    path: ['fee'],
  });

export type NewIncomingForm = z.infer<typeof NewIncomingFormSchema>;

/**
 * The outgoing-wire form (#272).
 *
 * Only one amount here, unlike an incoming: what left the client's wallet
 * is what left. The fee rides on top of it rather than being carved out of
 * it, and it may be charged in a different currency — a client billed in
 * dollars for a euro wire.
 */
export const NewOutgoingFormSchema = z
  .object({
    clientId: z.number({ required_error: 'Pick a client' }),
    valueDate: z.string().min(1, 'Date is required'),
    currencyCode: z.string().length(3, 'Currency is required'),
    amount: decimalString('Amount').refine((v) => Number(v) > 0, 'Amount must be more than zero'),
    fee: z.union([decimalString('Fee'), z.literal('')]).optional(),
    feeCurrencyCode: z.string().optional(),
    beneficiaryId: z.string().optional(),
    reference: z.string().max(80).optional(),
    description: z.string().max(255).optional(),
    sameClient: z.boolean(),
  })
  .refine((v) => (v.beneficiaryId ?? '').length > 0, {
    // `payment.beneficiary_id` is NOT NULL: a wire with no payee cannot be
    // stored, whoever it went to.
    message: 'Pick the beneficiary the wire went to',
    path: ['beneficiaryId'],
  });

export type NewOutgoingForm = z.infer<typeof NewOutgoingFormSchema>;

/** Which way round the operator is typing the rate (#273). */
export const RATE_DIRECTION = {
  SELL_TO_BUY: 'sell_to_buy',
  BUY_TO_SELL: 'buy_to_sell',
} as const;

export type RateDirection = (typeof RATE_DIRECTION)[keyof typeof RATE_DIRECTION];

const rateString = (field: string) =>
  z.string().regex(/^\d{1,12}(\.\d{1,8})?$/, `${field} must be a rate with up to 8 decimals`);

/**
 * The internal FX form (#273).
 *
 * Two rates: what the liquidity provider gave Montelux, and what the
 * client was quoted. Everything else on the screen — what the client
 * receives, what the trade costs, the margin — is worked out from them by
 * the server, so the recap an operator reads is the computation that will
 * run when they commit.
 */
export const NewConversionFormSchema = z
  .object({
    clientId: z.number({ required_error: 'Pick a client' }),
    valueDate: z.string().min(1, 'Date is required'),
    sellAmount: decimalString('Amount').refine(
      (v) => Number(v) > 0,
      'Amount must be more than zero',
    ),
    sellCurrencyCode: z.string().length(3),
    buyCurrencyCode: z.string().length(3),
    rateDirection: z.enum([RATE_DIRECTION.SELL_TO_BUY, RATE_DIRECTION.BUY_TO_SELL]),
    lpRate: rateString('LP rate').refine((v) => Number(v) > 0, 'LP rate must be more than zero'),
    clientRate: rateString('Client rate').refine(
      (v) => Number(v) > 0,
      'Client rate must be more than zero',
    ),
    marginCurrencyCode: z.string().length(3),
    description: z.string().max(255).optional(),
  })
  .refine((v) => v.sellCurrencyCode !== v.buyCurrencyCode, {
    message: 'A conversion needs two different currencies',
    path: ['buyCurrencyCode'],
  });

export type NewConversionForm = z.infer<typeof NewConversionFormSchema>;
